// §11.14 (D-335) — the alarm schedule, owned by the **shell** rather than by the app.
//
// This is the whole reason the app is not just a component with `useState`. An alarm that only
// exists while its window is open is not an alarm; it is a countdown you have to babysit. The
// schedule therefore lives in the kernel, where it keeps running with the window closed, keeps
// running with the app uninstalled from the taskbar, and rings through §13 — which is the surface
// that already knows how to reach somebody who is looking at something else.
//
// **What it still cannot do is ring while the tab is shut**, and nothing in the browser would let
// it: there is no service worker wake-up here, and D-015's instinct says we are not adding a
// push-notification dependency to a desktop that runs on one host. So the honest behaviour is the
// one the model was built for — an alarm whose instant passed while the tab was closed **rings on
// the next boot, and says how late it is** (`lateBy`). Late is useful; silent is not.
//
// State goes to a **file** (`/Apps/alarms/state.json`) for the reason D-275 gives for Conquest: the
// §5.6 session blob is keyed by window instance, so it survives a tab restore and cannot survive
// the window being closed — which is exactly the case an alarm has to survive.

import {
  IDLE_STOPWATCH,
  afterRing,
  computeNextAt,
  createAlarm,
  dueAlarms,
  lapWatch,
  lateBy,
  resetWatch,
  setEnabled,
  snooze as snoozeAlarm,
  startWatch,
  stopWatch,
  type Alarm,
  type Stopwatch,
  type Weekday,
} from '@hangar/shared';

/** Where the schedule lives. Under `/Apps/<id>/`, exactly as every other app's own data does. */
export const ALARMS_PATH = '/Apps/alarms/state.json';

/** How often the schedule is checked. One second is the coarsest tick a clock can be honest at. */
export const ALARM_TICK_MS = 1_000;

export interface AlarmsSnapshot {
  readonly alarms: readonly Alarm[];
  readonly stopwatch: Stopwatch;
  /** Rings that have not been dismissed yet — what the app shows and what the shell has announced. */
  readonly ringing: readonly string[];
}

export interface AlarmsStore {
  read(): Promise<unknown>;
  write(value: unknown): Promise<void>;
}

export interface AlarmsServiceOptions {
  readonly store: AlarmsStore;
  /** §13 — how a ring reaches somebody. Injected so a test never needs the notify service. */
  readonly announce: (alarm: Alarm, lateMs: number) => void;
  readonly now?: () => number;
  readonly setTimer?: (fn: () => void, ms: number) => number;
  readonly clearTimer?: (handle: number) => void;
  readonly newId?: () => string;
  readonly onError?: (error: unknown) => void;
}

export interface AlarmsService {
  snapshot(): AlarmsSnapshot;
  subscribe(listener: () => void): () => void;
  /** Read the file and arm everything. Idempotent; safe to call once at boot. */
  hydrate(): Promise<void>;
  /** Begin checking. Idempotent. */
  start(): void;
  stop(): void;

  add(fields: { label: string; hour: number; minute: number; days?: readonly Weekday[] }): Alarm;
  remove(id: string): void;
  toggle(id: string, enabled: boolean): void;
  snooze(id: string): void;
  /** Answer a ring: it stops sounding and the alarm keeps its schedule. */
  dismiss(id: string): void;

  startStopwatch(): void;
  stopStopwatch(): void;
  lapStopwatch(): void;
  resetStopwatch(): void;
}

interface StoredState {
  readonly v?: number;
  readonly alarms?: unknown;
  readonly stopwatch?: unknown;
}

/**
 * A stored alarm, checked field by field.
 *
 * The file is on the user's own filesystem and syncs between their devices (§7.4), so it is a
 * value that arrives from outside this process and gets read the way any other does. A row that
 * does not survive this is dropped rather than repaired: a half-understood alarm is one that rings
 * at the wrong time, which is worse than one that is missing.
 */
function readAlarm(value: unknown): Alarm | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;
  const id = raw['id'];
  const hour = raw['hour'];
  const minute = raw['minute'];
  if (typeof id !== 'string' || typeof hour !== 'number' || typeof minute !== 'number') return null;
  const days = Array.isArray(raw['days'])
    ? (raw['days'].filter((day) => typeof day === 'number' && day >= 0 && day <= 6) as Weekday[])
    : [];
  const number = (key: string): number | null =>
    typeof raw[key] === 'number' ? (raw[key] as number) : null;
  return {
    id,
    label: typeof raw['label'] === 'string' ? raw['label'] : '',
    hour,
    minute,
    days,
    enabled: raw['enabled'] !== false,
    nextAt: number('nextAt'),
    snoozedUntil: number('snoozedUntil'),
    lastRangAt: number('lastRangAt'),
  };
}

function readStopwatch(value: unknown): Stopwatch {
  if (typeof value !== 'object' || value === null) return IDLE_STOPWATCH;
  const raw = value as Record<string, unknown>;
  const laps = Array.isArray(raw['laps'])
    ? raw['laps'].filter((lap): lap is number => typeof lap === 'number')
    : [];
  return {
    running: raw['running'] === true,
    startedAt: typeof raw['startedAt'] === 'number' ? raw['startedAt'] : null,
    elapsedMs: typeof raw['elapsedMs'] === 'number' ? raw['elapsedMs'] : 0,
    laps,
  };
}

export function createAlarmsService(options: AlarmsServiceOptions): AlarmsService {
  const now = options.now ?? (() => Date.now());
  const setTimer =
    options.setTimer ?? ((fn, ms) => globalThis.setInterval(fn, ms) as unknown as number);
  const clearTimer = options.clearTimer ?? ((handle) => globalThis.clearInterval(handle));
  const newId = options.newId ?? (() => `a_${Math.floor(Date.now()).toString(36)}`);
  const report = options.onError ?? ((error: unknown) => console.warn('alarms', error));

  let alarms: readonly Alarm[] = [];
  let stopwatch: Stopwatch = IDLE_STOPWATCH;
  let ringing: readonly string[] = [];
  let timer: number | null = null;
  let hydrated = false;
  const listeners = new Set<() => void>();

  const ring = (): void => {
    for (const listener of [...listeners]) {
      try {
        listener();
      } catch (error) {
        report(error);
      }
    }
  };

  /**
   * Write, then tell everyone.
   *
   * The write is not awaited: a schedule change has to feel instant, and a filesystem that is slow
   * or full must not make the toggle stick. A failed write is reported and the next change tries
   * again — the cost of losing one is an alarm that reverts on the next boot, not a wrong ring.
   */
  const commit = (): void => {
    ring();
    if (!hydrated) return;
    void options.store
      .write({ v: 1, alarms, stopwatch })
      .catch((error: unknown) => report(error));
  };

  /** Fire everything due, announce it, and re-arm. */
  const check = (): void => {
    const at = now();
    const due = dueAlarms(alarms, at);
    if (due.length === 0) return;
    const fired = new Set(due.map((alarm) => alarm.id));
    // Announce **before** advancing, so `lateBy` still has the instant it was due to measure from.
    for (const alarm of due) options.announce(alarm, lateBy(alarm, at));
    alarms = alarms.map((alarm) => (fired.has(alarm.id) ? afterRing(alarm, at) : alarm));
    ringing = [...new Set([...ringing, ...fired])];
    commit();
  };

  const update = (id: string, change: (alarm: Alarm) => Alarm): void => {
    let touched = false;
    alarms = alarms.map((alarm) => {
      if (alarm.id !== id) return alarm;
      touched = true;
      return change(alarm);
    });
    if (touched) commit();
  };

  return {
    snapshot: () => ({ alarms, stopwatch, ringing }),

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    async hydrate() {
      if (hydrated) return;
      try {
        const blob = (await options.store.read()) as StoredState | null;
        if (blob !== null && typeof blob === 'object') {
          alarms = Array.isArray(blob.alarms)
            ? (blob.alarms.map(readAlarm).filter((alarm): alarm is Alarm => alarm !== null))
            : [];
          stopwatch = readStopwatch(blob.stopwatch);
        }
      } catch (error) {
        // No file yet is the ordinary case on a first run, and an unreadable one must not stop the
        // desktop: an empty schedule is a working app, and the next write repairs the file.
        report(error);
      }
      /**
       * Re-arm anything that lost its instant.
       *
       * An enabled alarm with no `nextAt` is one written by an older build, or one whose file was
       * hand-edited. Computing it here rather than trusting the file is what keeps "enabled" and
       * "will ring" the same statement.
       */
      const at = now();
      alarms = alarms.map((alarm) =>
        alarm.enabled && alarm.nextAt === null && alarm.snoozedUntil === null
          ? { ...alarm, nextAt: computeNextAt(alarm, at) }
          : alarm,
      );
      hydrated = true;
      // Catch up on anything that came due while the tab was shut, before the first paint.
      check();
      ring();
    },

    start() {
      if (timer !== null) return;
      check();
      timer = setTimer(check, ALARM_TICK_MS);
    },

    stop() {
      if (timer === null) return;
      clearTimer(timer);
      timer = null;
    },

    add(fields) {
      const alarm = createAlarm(newId(), fields, now());
      alarms = [...alarms, alarm];
      commit();
      return alarm;
    },

    remove(id) {
      const before = alarms.length;
      alarms = alarms.filter((alarm) => alarm.id !== id);
      ringing = ringing.filter((one) => one !== id);
      if (alarms.length !== before) commit();
    },

    toggle(id, enabled) {
      update(id, (alarm) => setEnabled(alarm, enabled, now()));
      ringing = ringing.filter((one) => one !== id);
    },

    snooze(id) {
      update(id, (alarm) => snoozeAlarm(alarm, now()));
      ringing = ringing.filter((one) => one !== id);
      commit();
    },

    dismiss(id) {
      if (!ringing.includes(id)) return;
      ringing = ringing.filter((one) => one !== id);
      commit();
    },

    startStopwatch() {
      stopwatch = startWatch(stopwatch, now());
      commit();
    },
    stopStopwatch() {
      stopwatch = stopWatch(stopwatch, now());
      commit();
    },
    lapStopwatch() {
      stopwatch = lapWatch(stopwatch, now());
      commit();
    },
    resetStopwatch() {
      stopwatch = resetWatch();
      commit();
    },
  };
}
